import { useCallback, useState, type ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { PendingRegion } from '@/shared/components/PendingRegion';

/**
 * The frame for the workflow flow dialogs: Export, Upload, Copy to workspace and
 * Share to Library (spec 207).
 *
 * **Its top never moves, and it never shrinks while open.** Those dialogs grow by
 * hundreds of pixels when a preview or a check returns, and a centred dialog that
 * grows moves its top edge: the review measured the title jumping 295px under the
 * cursor. So the top is anchored at `8vh`. The height follows the content, up to
 * `min(720px, 85vh)`, but only ever grows within one opening: what it reached is
 * held as its minimum, so ticking, folding or a shorter answer never pulls the
 * footer back up. It was a fixed 720px until dialogs review 2, which left Export
 * and Copy as tall cards around 150px of content. Past the cap, the body between
 * header and footer is the one part that scrolls.
 *
 * **The footer is the frame's, not the caller's.** Cancel is drawn here and the
 * caller passes exactly one primary button, so no face of any of these dialogs can
 * leave its next action in the body (Upload's bundle face once did). A failure of
 * that action shows directly above the footer, where it is seen without scrolling.
 *
 * **On a phone it is the whole screen**, with the footer pinned to the bottom. Its
 * bottom padding allows for the safe area, though `index.html` does not set
 * `viewport-fit=cover`, so today the browser keeps the page clear of it and `env()`
 * reads 0. Opting in would move every fixed element in the app, which is out of
 * this frame's scope; the padding is simply right if that ever happens.
 *
 * While `busy`, Cancel is disabled and nothing closes the dialog, as each of these
 * dialogs used to arrange by hand.
 */
export function FlowDialog({
  open,
  onOpenChange,
  title,
  description,
  primary,
  alert,
  busy = false,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description: ReactNode;
  /** The one primary button. Cancel is drawn by the frame, before it. */
  primary: ReactNode;
  /** A failure of the primary action, shown directly above the footer. */
  alert?: ReactNode;
  /** While true, Cancel is disabled and the dialog refuses to close. */
  busy?: boolean;
  /** The body: the only part that scrolls. */
  children: ReactNode;
}) {
  const { ref: frameRef, style: frameStyle } = useGrowOnly(open);
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (busy && !next) return;
        onOpenChange(next);
      }}
    >
      <DialogContent
        ref={frameRef}
        style={frameStyle}
        className="top-[8vh] flex max-h-[min(720px,85vh)] translate-y-0 flex-col gap-0 overflow-hidden p-0 sm:max-w-lg"
        // A flow with steps: the whole screen on a phone, as it was before
        // every dialog became a bottom sheet there (spec 261).
        phoneLayout="full"
      >
        <DialogHeader className="shrink-0 border-b border-border px-5 pt-4 pb-3 pr-12">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div
          data-slot="flow-dialog-body"
          className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-5 py-4"
        >
          {children}
        </div>
        {alert ? (
          <div
            role="alert"
            className="text-destructive shrink-0 border-t border-border px-5 py-2 text-sm"
          >
            {alert}
          </div>
        ) : null}
        <DialogFooter
          flush
          className="pb-[max(0.875rem,env(safe-area-inset-bottom))]"
        >
          <DialogClose render={<Button variant="outline" disabled={busy} />}>
            Cancel
          </DialogClose>
          {primary}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The frame's height so far in this opening, held as its minimum.
 *
 * Measured, not computed: the content's height is the sum of whatever the dialog
 * put in it. Capped by the same `min(720px, 85vh)` as the frame, since a minimum
 * above the maximum would win and push the footer off a window made smaller. It
 * starts again from nothing at each opening.
 */
function useGrowOnly(open: boolean) {
  const [height, setHeight] = useState(0);
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setHeight(0);
  }
  // A ref callback with its own cleanup (React 19): the observer lives as long
  // as the node it watches.
  const ref = useCallback((node: HTMLDivElement | null) => {
    if (!node || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      const next = Math.ceil(node.getBoundingClientRect().height);
      setHeight((held) => (next > held ? next : held));
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return {
    ref,
    style:
      height > 0 ? { minHeight: `min(${String(height)}px, 720px, 85vh)` } : {},
  };
}

/**
 * What a flow dialog's body shows while its preview or check is on the way.
 *
 * Rows the height of the real ones (36px), under a short visible label in today's
 * words. The label is announced once, by the region; its visible copy is hidden from
 * assistive technology so it is not read twice.
 */
export function FlowDialogPending({
  label,
  rows = 4,
}: {
  label: string;
  rows?: number;
}) {
  return (
    <PendingRegion label={label} className="gap-2">
      <p aria-hidden="true" className="text-muted-foreground text-sm">
        {label}
      </p>
      {Array.from({ length: rows }, (_, index) => (
        <Skeleton key={index} className="h-9 w-full" />
      ))}
    </PendingRegion>
  );
}
